import { KeyRound } from 'lucide-react';
import { useId } from 'react';
import { useTranslation } from 'react-i18next';
import { LastUsedBadge } from '@/Domain/components/Auth/LastUsedBadge';
import { Alert, AlertDescription } from '@/Domain/components/Shared/Alert/Alert';
import { Button } from '@/Domain/components/Shared/Button/Button';
import { Spinner } from '@/Domain/components/Shared/Spinner';
import { usePasskeyLogin } from '@/Domain/hooks/Auth/usePasskeyLogin.hook';

export const PasskeyLoginButton = ({ isLastUsed = false }: { isLastUsed?: boolean }) => {
  const { t } = useTranslation('login');
  const { isSupported, signInWithPasskey, isPending, errorMessage } = usePasskeyLogin();
  const badgeId = useId();

  if (!isSupported) {
    return null;
  }

  return (
    <>
      <div className="relative">
        <Button
          type="button"
          variant="secondary"
          size="lg"
          onClick={signInWithPasskey}
          disabled={isPending}
          className="w-full gap-3 font-medium"
          aria-describedby={isLastUsed ? badgeId : undefined}
          data-testid="login-passkey"
        >
          {isPending ? <Spinner /> : <KeyRound className="h-5 w-5" />}
          <span>{t('passkey.button')}</span>
        </Button>
        {isLastUsed && <LastUsedBadge id={badgeId} />}
      </div>
      {errorMessage && (
        <Alert
          variant="destructive"
          data-testid="login-passkey-error"
          className="animate-in fade-in duration-200"
        >
          <AlertDescription>{errorMessage}</AlertDescription>
        </Alert>
      )}
    </>
  );
};
